import { debug } from 'debug';

const log = debug('cartsee:sign');

/**
 * 检测是否支持 Web Crypto API
 */
function supportsWebCrypto(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof crypto !== 'undefined' &&
    typeof crypto.subtle !== 'undefined'
  );
}

/**
 * 使用 Web Crypto API 计算 SHA-256（现代浏览器）
 * @param message 要哈希的消息
 * @returns Promise<string> 十六进制小写哈希值
 */
async function sha256HexWebCrypto(message: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(message);

  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * 使用 crypto-js 计算 SHA-256（兼容老浏览器）
 * @param message 要哈希的消息
 * @returns Promise<string> 十六进制小写哈希值
 */
async function sha256HexFallback(message: string): Promise<string> {
  try {
    // 按需动态加载，避免影响首屏体积
    const CryptoJS = await import('crypto-js');
    const hash = CryptoJS.SHA256(message);
    return hash.toString(CryptoJS.enc.Hex).toLowerCase();
  } catch (error) {
    log('Failed to load crypto-js fallback:', error);
    throw new Error('SHA-256 calculation failed: crypto-js not available');
  }
}

/**
 * 计算 SHA-256 哈希并返回十六进制小写字符串（自动选择最佳方案）
 * @param message 要哈希的消息
 * @returns Promise<string> 十六进制小写哈希值
 */
export async function sha256Hex(message: string): Promise<string> {
  if (supportsWebCrypto()) {
    try {
      return await sha256HexWebCrypto(message);
    } catch (error) {
      log('Web Crypto API failed, falling back to crypto-js:', error);
      return await sha256HexFallback(message);
    }
  }

  log('Web Crypto API not supported, using crypto-js fallback');
  return await sha256HexFallback(message);
}

/**
 * 生成接口签名：SHA256(requestBody + appKey) -> hex 小写
 * @param requestBody 请求体 JSON 字符串
 * @param appKey      后端分配的密钥
 */
export async function generateSign(
  requestBody: string,
  appKey: string,
): Promise<string> {
  const messageForDigest = requestBody + appKey;
  return await sha256Hex(messageForDigest);
}

